// useMyTenants — the caller's tenant ids, fetched once and cached for the
// session (success only). Both the command palette (AppShell) and the sidebar
// TenantSwitcher read it, so the module cache dedupes the GET /v1/me/tenants
// call and keeps them in sync. A transient fetch failure is NOT cached: the
// hook returns [] and leaves the cache empty so the next mount retries —
// caching an empty list from a blip would hide the switcher for the session.

import { useEffect, useState } from "react";

import { tenantsApi } from "@/api/tenants";
import { useAccessToken } from "@/auth/useAccessToken";

let cache: string[] | null = null;
let inflight: Promise<string[]> | null = null;

export function _primeMyTenantsCache(value: string[] | null): void {
  cache = value;
  inflight = null;
}

export function useMyTenants(): string[] {
  const { acquireToken, accounts } = useAccessToken();
  const [tenants, setTenants] = useState<string[]>(() => cache ?? []);

  useEffect(() => {
    if (cache) {
      setTenants(cache);
      return;
    }
    if (accounts.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        if (!inflight) {
          inflight = (async () => {
            const token = await acquireToken();
            return tenantsApi.myTenants(token);
          })();
        }
        const result = await inflight;
        cache = result;
        if (!cancelled) setTenants(result);
      } catch (err) {
        inflight = null;
        // eslint-disable-next-line no-console
        console.warn("[useMyTenants] failed to load tenants", err);
        if (!cancelled) setTenants([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [acquireToken, accounts.length]);

  return tenants;
}
